import { Map as MapLibreMap, setWorkerUrl } from 'maplibre-gl';
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import { createContext, use, useEffect, useMemo, useRef, useState } from 'react';
import { mapStyleFor, DEFAULT_CENTER, DEFAULT_ZOOM } from './map-styles';
import { registerFleetIcons } from './fleet-icons';
import { useTheme } from '@/providers/theme-provider';
import { useUiStore } from '@/stores/ui-store';

/**
 * MapLibre 6 ships its worker as a separate file and looks for it next to its
 * own module. Bundled, "its own module" is our main chunk, and the worker file
 * is not emitted beside it, so in a production build the request fell through
 * to the SPA fallback and came back as index.html. The worker never started,
 * no GeoJSON source ever tiled, and every layer of ours (vehicle markers,
 * geofences, routes) silently drew nothing over a basemap that looked fine.
 *
 * `?worker&url` has Vite build the worker, with the code it imports, as an
 * asset of its own and hand back the real URL.
 */
setWorkerUrl(maplibreWorkerUrl);

interface MapContextValue {
  map: MapLibreMap | null;
  /** True once the style has loaded and custom sources may be added. */
  ready: boolean;
  /** Bumped on every style swap so layer effects re-add themselves. */
  styleEpoch: number;
}

const MapContext = createContext<MapContextValue>({ map: null, ready: false, styleEpoch: 0 });

export const useMap = () => use(MapContext);

export function MapProvider({
  children,
  className,
  interactive = true,
  initialCenter = DEFAULT_CENTER,
  initialZoom = DEFAULT_ZOOM,
  attributionCompact = true,
}: {
  children?: React.ReactNode;
  className?: string;
  interactive?: boolean;
  initialCenter?: [number, number];
  initialZoom?: number;
  attributionCompact?: boolean;
}) {
  const container = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const [map, setMap] = useState<MapLibreMap | null>(null);
  const [ready, setReady] = useState(false);
  const [styleEpoch, setStyleEpoch] = useState(0);

  const baseLayer = useUiStore((s) => s.baseLayer);
  const { resolved } = useTheme();

  /**
   * Which style the live instance is actually showing. Comparing against this
   * — rather than a "have I run yet" flag — is what stops StrictMode's double
   * effect from calling setStyle while the first style is still loading, which
   * rebuilds the style from scratch and silently drops our sources and icons.
   */
  const styleKey = `${baseLayer}:${resolved}`;
  const appliedStyleKey = useRef<string | null>(null);

  // Created once. Style and theme changes are applied to the live instance
  // rather than remounting the canvas, which would drop the viewport.
  useEffect(() => {
    if (!container.current || mapRef.current) return;

    const initialLayer = useUiStore.getState().baseLayer;
    appliedStyleKey.current = `${initialLayer}:${resolved}`;

    const instance = new MapLibreMap({
      container: container.current,
      style: mapStyleFor(initialLayer, resolved),
      center: initialCenter,
      zoom: initialZoom,
      interactive,
      attributionControl: { compact: attributionCompact },
      fadeDuration: 120,
      maxZoom: 19,
      // Marker rotation is done in the icon, so a rotated map only confuses.
      dragRotate: false,
      pitchWithRotate: false,
      touchZoomRotate: true,
    });
    instance.touchZoomRotate.disableRotation();

    // Style, source and worker failures surface here rather than silently
    // leaving a layer blank.
    instance.on('error', (event) => {
      console.error('[maplibre]', event.error?.message ?? event);
    });

    instance.on('load', () => {
      registerFleetIcons(instance, resolved);
      setReady(true);
    });

    mapRef.current = instance;
    setMap(instance);

    // Handy from the console when a layer is not drawing; stripped in prod.
    if (import.meta.env.DEV) {
      (window as unknown as { __vtrackMap?: MapLibreMap }).__vtrackMap = instance;
    }

    return () => {
      instance.remove();
      mapRef.current = null;
      setMap(null);
      setReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const instance = mapRef.current;
    if (!instance || appliedStyleKey.current === styleKey) return;

    let cancelled = false;

    const apply = () => {
      if (cancelled) return;
      appliedStyleKey.current = styleKey;
      setReady(false);
      instance.setStyle(mapStyleFor(baseLayer, resolved));
      // `idle` rather than `styledata`: styledata fires repeatedly while tiles
      // stream in, and re-adding layers against a half-built style throws.
      instance.once('idle', () => {
        if (cancelled) return;
        registerFleetIcons(instance, resolved);
        setStyleEpoch((epoch) => epoch + 1);
        setReady(true);
      });
    };

    if (instance.isStyleLoaded()) apply();
    else instance.once('idle', apply);

    return () => {
      cancelled = true;
    };
  }, [styleKey, baseLayer, resolved]);

  const value = useMemo<MapContextValue>(() => ({ map, ready, styleEpoch }), [map, ready, styleEpoch]);

  return (
    <MapContext value={value}>
      <div ref={container} className={className} />
      {ready && children}
    </MapContext>
  );
}
